import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  CheckCircle2,
  Download,
  Github,
  DollarSign,
  Code2,
  Users,
  Zap,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePurchase = async () => {
    try {
      setLoading(true);
      setError('');

      // Get user email from localStorage if logged in
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const email = user.email || null;

      // Create checkout session - email is optional
      // If user is logged in, pass email to associate with existing customer
      // If user is not logged in, Stripe will collect email during checkout
      const requestBody = {
        tier: 'pro',
        success_url: `${window.location.origin}/purchase/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/pricing`
      };

      // Only include email if user is logged in
      if (email) {
        requestBody.email = email;
      }

      const response = await axios.post(`${API_BASE_URL}/api/checkout/create-session`, requestBody);

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const features = [
    "Full access to all features",
    "105 MCP tools + 27 internal tools",
    "Multi-provider AI support (OpenAI, OpenRouter, Gemini, Local LLMs)",
    "Browser automation with Playwright",
    "Hooks system with 11 hook events",
    "Bug reproduction assistant",
    "Recall activity tracking & search",
    "Scheduled tasks & PR reviews",
    "Token tracking & cost monitoring",
    "System-wide dictation",
    "Desktop commander tools",
    "SQL database connections",
    "Azure DevOps integration",
    "Dependency mapping with visualizations",
    "Persistent sessions with categories",
    "Custom system prompts",
    "Export chat history",
    "Cross-platform support",
    "All future updates free for your version",
  ];

  return (
    <div className="min-h-screen pt-20 pb-20">
      <section className="py-20 bg-gradient-to-b from-purple-950/10 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {error && (
            <Alert variant="destructive" className="mb-8 max-w-3xl mx-auto">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <h1 className="text-5xl md:text-6xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Simple Pricing
              </span>
            </h1>
            <p className="text-xl text-foreground/70">
              Buy once, keep forever. All upgrades for your version are always free.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden bg-gradient-to-br from-purple-950/30 to-purple-900/30 border-purple-500/20 shadow-2xl">
              <CardHeader className="text-center pb-8 pt-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-purple-400" />
                </div>
                <CardTitle className="text-4xl font-bold mb-2">
                  <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                    Buy Once, Keep Forever
                  </span>
                </CardTitle>

                {/* Product Hunt Badge */}
                <div className="flex justify-center mb-6">
                  <a href="https://www.producthunt.com/products/cogsmith?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-cogsmith" target="_blank" rel="noopener noreferrer">
                    <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1034955&theme=light&t=1762481229275" alt="Cogsmith - AI Desktop Assistant for Coders & QA Analysts | Product Hunt" style={{ width: '250px', height: '54px' }} width="250" height="54" />
                  </a>
                </div>

                {/* Beta Pricing Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-purple-500/20 border border-yellow-500/30 mb-4">
                  <Zap className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm font-semibold bg-gradient-to-r from-yellow-400 to-purple-400 bg-clip-text text-transparent">
                    PRODUCT HUNT LAUNCH SPECIAL
                  </span>
                </div>

                {/* Pricing Display */}
                <div className="flex items-baseline justify-center space-x-4 mb-2">
                  {/* Regular Price */}
                  <span className="text-7xl font-bold bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                    $199
                  </span>
                  <span className="text-foreground/60">one-time</span>
                </div>

                {/* Coupon Code Notice */}
                <div className="max-w-md mx-auto space-y-3 mt-4">
                  <div className="bg-gradient-to-r from-yellow-500/20 to-purple-500/20 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-lg font-bold text-yellow-400 mb-2">
                      🎉 Use code: <span className="text-2xl font-mono">PHLAUNCH2025</span>
                    </p>
                    <p className="text-foreground/80 text-sm">
                      Get it for just <span className="text-xl font-bold text-green-400">$29</span> at checkout!
                    </p>
                  </div>
                  <p className="text-foreground/70">
                    Purchase once and own your version forever. All updates for your version included at no extra cost.
                  </p>
                </div>
              </CardHeader>
              <CardContent className="px-8 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/80 text-sm">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button
                    onClick={handlePurchase}
                    disabled={loading}
                    size="lg"
                    className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300"
                  >
                    <DollarSign className="mr-2 h-5 w-5" />
                    {loading ? 'Loading...' : 'Purchase Pro - $199'}
                  </Button>
                  <Link to="/download" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Try Free Version
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Why Buy Once, Keep Forever?
              </span>
            </h2>
            <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
              We believe in sustainable, fair software pricing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 transition-colors">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center mx-auto">
                  <Code2 className="h-8 w-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold">No Subscriptions</h3>
                <p className="text-foreground/60">
                  Pay once and own your version forever without recurring monthly fees
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 transition-colors">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center mx-auto">
                  <Zap className="h-8 w-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold">Free Updates</h3>
                <p className="text-foreground/60">
                  All upgrades and improvements for your version are included at no extra cost
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30 transition-colors">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center mx-auto">
                  <Users className="h-8 w-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold">Fair Pricing</h3>
                <p className="text-foreground/60">
                  Old-school software model: buy what you need, keep what you buy
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-background to-purple-950/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-3xl mx-auto bg-card/50 backdrop-blur-sm border-border/40">
            <CardContent className="p-8 md:p-12 text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                  API Keys Required
                </span>
              </h2>
              <p className="text-lg text-foreground/70">
                While Cogsmith uses a one-time purchase model, you'll need your own API keys for AI providers like OpenAI, OpenRouter, or Gemini. You only pay for what you use directly to the provider.
              </p>
              <div className="bg-purple-950/20 border border-purple-500/20 rounded-lg p-6">
                <p className="text-foreground/80 text-sm">
                  <strong className="text-purple-400">💡 Tip:</strong> With
                  built-in token tracking and cost monitoring, you'll always
                  know exactly how much you're spending on API calls.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
